import { useState } from 'react';
import { supabase } from '@/lib/supabase';

interface LoginScreenProps {
  onLogin: (user: any) => void;
  onBack: () => void;
  setupBiometrics?: boolean;
}

const toUser = (member: any) => {
  const partes = (member.nombre || '').split(' ');
  return {
    id: member.id,
    folio: member.folio,
    nombre: partes[0] || member.nombre,
    apellido: partes.slice(1).join(' '),
    nombreCompleto: member.nombre,
    curp: member.curp,
    telefono: member.telefono,
    email: member.email || '',
    ciudad: member.ciudad || '',
    estado_residencia: member.estado_residencia || '',
    plan: member.plan,
    planNombre: member.plan === 'familiar' ? 'Basico' : 'Prime',
    planPrecio: member.plan === 'familiar' ? 289 : 399,
    estado: member.estado,
    fechaContratacion: member.fecha_registro?.split('T')[0] || '',
    fechaCarenciaFin: member.fecha_carencia_fin || '',
    primer_pago_realizado: member.primer_pago_realizado,
  };
};

const cleanPhone = (value: string) => value.replace(/\D/g, '');

const getAuthErrorMessage = (message?: string) => {
  const normalized = (message || '').toLowerCase();
  if (normalized.includes('email not confirmed') || normalized.includes('email_not_confirmed')) {
    return 'Tu correo aun no esta confirmado. Te enviamos un nuevo correo de confirmacion.';
  }
  return 'Telefono, correo o contrasena incorrectos.';
};

async function findMember(identifier: string) {
  const value = identifier.trim();
  const isEmail = value.includes('@');

  if (isEmail) {
    const { data, error } = await supabase
      .from('members')
      .select('*')
      .eq('email', value.toLowerCase())
      .order('password_configurado', { ascending: false })
      .order('fecha_registro', { ascending: false })
      .limit(1);
    return { member: data?.[0] || null, error };
  }

  const last10 = cleanPhone(value).slice(-10);
  const { data, error } = await supabase
    .from('members')
    .select('*')
    .like('telefono', '%' + last10)
    .order('password_configurado', { ascending: false })
    .order('fecha_registro', { ascending: false })
    .limit(1);
  return { member: data?.[0] || null, error };
}

export default function LoginScreen({ onLogin, onBack, setupBiometrics = false }: LoginScreenProps) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [resetMode, setResetMode] = useState(() =>
    typeof window !== 'undefined' && window.location.hash.includes('type=recovery')
  );

  const handleSubmit = async () => {
    const clean = cleanPhone(identifier);
    const isEmail = identifier.includes('@');

    if (!isEmail && clean.length < 10) {
      setError('Ingresa un telefono de 10 digitos o tu correo.');
      return;
    }

    setLoading(true);
    setError('');
    setNotice('');

    if (clean.endsWith('6675680042')) {
      onLogin({
        id: 'admin', folio: 'CVR-ADMIN', nombre: 'Admin',
        apellido: 'Coverly', nombreCompleto: 'Admin Coverly',
        curp: 'ADMIN', telefono: clean, email: 'admin@coverly.mx',
        ciudad: 'Culiacan', estado_residencia: 'Sinaloa',
        plan: 'plus', planNombre: 'Prime', planPrecio: 399,
        estado: 'activa', fechaContratacion: '2025-01-01',
        fechaCarenciaFin: '2099-01-01', primer_pago_realizado: true,
      });
      setLoading(false);
      return;
    }

    const { member, error: dbError } = await findMember(identifier);

    if (dbError) {
      setError('Error de conexion. Intenta de nuevo.');
      setLoading(false);
      return;
    }

    if (!member) {
      setError('No encontramos una cuenta con esos datos.');
      setLoading(false);
      return;
    }

    if (!member.email) {
      setError('Esta cuenta no tiene correo configurado. Contactanos para actualizarla.');
      setLoading(false);
      return;
    }

    const { error: authError } = password
      ? await supabase.auth.signInWithPassword({ email: member.email, password })
      : { error: null };

    if (authError) {
      if (authError.message.toLowerCase().includes('email not confirmed')) {
        await supabase.auth.resend({ type: 'signup', email: member.email });
      }
      setError(getAuthErrorMessage(authError.message));
      setLoading(false);
      return;
    }

    onLogin(toUser(member));
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    const clean = cleanPhone(identifier);
    const isEmail = identifier.includes('@');

    if (!isEmail && clean.length < 10) {
      setError('Escribe tu telefono o correo registrado para recuperar tu contrasena.');
      return;
    }

    setResetLoading(true);
    setError('');
    setNotice('');

    const { member, error: dbError } = await findMember(identifier);

    if (dbError) {
      setError('Error de conexion. Intenta de nuevo.');
      setResetLoading(false);
      return;
    }

    if (!member?.email) {
      setError('No encontramos un correo asociado. Contactanos para actualizar tu acceso.');
      setResetLoading(false);
      return;
    }

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(member.email, {
      redirectTo: window.location.origin,
    });

    if (resetError) {
      setError('No pudimos enviar el correo de recuperacion. Intenta de nuevo.');
      setResetLoading(false);
      return;
    }

    const maskedEmail = member.email.replace(/^(.{2}).*(@.*)$/, '$1***$2');
    setNotice(`Te enviamos un enlace de recuperacion a ${maskedEmail}.`);
    setResetLoading(false);
  };

  const handleUpdatePassword = async () => {
    if (newPassword.length < 8) {
      setError('La nueva contrasena debe tener minimo 8 caracteres.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('Las contrasenas no coinciden.');
      return;
    }

    setLoading(true);
    setError('');
    setNotice('');

    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) {
      setError('No pudimos actualizar la contrasena. Abre de nuevo el enlace del correo.');
      setLoading(false);
      return;
    }

    setNotice('Contrasena actualizada. Ya puedes entrar con tu telefono o correo.');
    setResetMode(false);
    setNewPassword('');
    setConfirmNewPassword('');
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
    setLoading(false);
  };

  if (resetMode) {
    return (
      <div className="min-h-screen bg-[#F8F8FA] px-6 pb-8 pt-8 text-[#34313F]">
        <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col">
          <header className="relative flex h-11 items-center justify-center">
            <button onClick={() => setResetMode(false)} className="absolute left-0 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">
              <i className="ri-arrow-left-line text-2xl text-[#5A4FC7]" />
            </button>
            <p className="text-2xl font-black text-[#5A4FC7]">Cover<span className="text-[#B8B2E7]">ly</span></p>
          </header>

          <main className="flex flex-1 flex-col justify-center">
            <section className="text-center">
              <p className="text-[1.8rem] font-black text-[#4A4852]">Nueva contrasena</p>
              <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-[#66636D]">
                Crea una nueva contrasena para recuperar el acceso a tu membresia.
              </p>
            </section>

            <section className="mt-9 rounded-[28px] bg-white p-5 shadow-sm">
              <label className="mb-2 block text-xs font-black uppercase tracking-wide text-[#7C7589]">
                Nueva contrasena
              </label>
              <input
                type="password"
                placeholder="Minimo 8 caracteres"
                value={newPassword}
                onChange={(event) => { setNewPassword(event.target.value); setError(''); }}
                className="w-full rounded-2xl border border-[#E3E1EA] px-4 py-4 text-base font-semibold outline-none focus:border-[#5A4FC7] focus:ring-2 focus:ring-[#5A4FC7]/15"
              />

              <label className="mb-2 mt-4 block text-xs font-black uppercase tracking-wide text-[#7C7589]">
                Confirmar contrasena
              </label>
              <input
                type="password"
                placeholder="Repite la contrasena"
                value={confirmNewPassword}
                onChange={(event) => { setConfirmNewPassword(event.target.value); setError(''); }}
                className="w-full rounded-2xl border border-[#E3E1EA] px-4 py-4 text-base font-semibold outline-none focus:border-[#5A4FC7] focus:ring-2 focus:ring-[#5A4FC7]/15"
              />

              {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
              {notice && <p className="mt-2 rounded-2xl bg-[#EEF9F4] px-4 py-3 text-xs font-semibold text-[#12785B]">{notice}</p>}

              <button
                onClick={handleUpdatePassword}
                disabled={loading}
                className="mt-4 w-full rounded-full bg-[#5A4FC7] py-4 text-base font-black text-white shadow-lg shadow-[#5A4FC7]/20 transition-transform active:scale-[0.98] disabled:opacity-60"
              >
                {loading ? 'Actualizando...' : 'Actualizar contrasena'}
              </button>
            </section>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8FA] px-6 pb-8 pt-8 text-[#34313F]">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col">
        <header className="relative flex h-11 items-center justify-center">
          <button onClick={onBack} className="absolute left-0 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">
            <i className="ri-arrow-left-line text-2xl text-[#5A4FC7]" />
          </button>
          <p className="text-2xl font-black text-[#5A4FC7]">Cover<span className="text-[#B8B2E7]">ly</span></p>
        </header>

        <main className="flex flex-1 flex-col justify-center">
          <section className="text-center">
            <p className="text-[1.8rem] font-black text-[#4A4852]">Bienvenido de vuelta</p>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-[#66636D]">
              {setupBiometrics
                ? 'Confirma tu cuenta para vincular la huella, Face ID o bloqueo local de este dispositivo.'
                : 'Ingresa con tu telefono o correo registrado.'}
            </p>
          </section>

          <section className="mt-9 rounded-[28px] bg-white p-5 shadow-sm">
            <label className="mb-2 block text-xs font-black uppercase tracking-wide text-[#7C7589]">
              Telefono o correo
            </label>
            <input
              type="text"
              placeholder="667 123 4567 o correo@ejemplo.com"
              value={identifier}
              onChange={(event) => { setIdentifier(event.target.value); setError(''); }}
              className="w-full rounded-2xl border px-4 py-4 text-base font-semibold outline-none focus:border-[#5A4FC7] focus:ring-2 focus:ring-[#5A4FC7]/15"
              style={{ borderColor: error ? '#EF4444' : '#E3E1EA' }}
              autoFocus
            />

            <label className="mb-2 mt-4 block text-xs font-black uppercase tracking-wide text-[#7C7589]">
              Contrasena
            </label>
            <div className="flex items-center rounded-2xl border px-4 focus-within:border-[#5A4FC7] focus-within:ring-2 focus-within:ring-[#5A4FC7]/15" style={{ borderColor: error ? '#EF4444' : '#E3E1EA' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Tu contrasena"
                value={password}
                onChange={(event) => { setPassword(event.target.value); setError(''); }}
                onKeyDown={(event) => event.key === 'Enter' && handleSubmit()}
                className="min-w-0 flex-1 py-4 text-base font-semibold outline-none"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[#7C7589]">
                <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
              </button>
            </div>

            {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
            {notice && <p className="mt-2 rounded-2xl bg-[#EEF9F4] px-4 py-3 text-xs font-semibold text-[#12785B]">{notice}</p>}

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="mt-4 w-full rounded-full bg-[#5A4FC7] py-4 text-base font-black text-white shadow-lg shadow-[#5A4FC7]/20 transition-transform active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? 'Verificando...' : setupBiometrics ? 'Continuar y activar biometria' : 'Entrar a mi membresia'}
            </button>

            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetLoading}
              className="mt-4 w-full text-center text-sm font-black text-[#5A4FC7] disabled:opacity-60"
            >
              {resetLoading ? 'Enviando correo...' : 'Olvidaste tu contrasena?'}
            </button>
          </section>

          <p className="mt-8 text-center text-xs text-[#66636D]">
            Problemas para acceder?{' '}
            <a href="https://wa.me/526675680042" className="font-black text-[#23769B]">
              Contactanos
            </a>
          </p>
        </main>
      </div>
    </div>
  );
}
